<div class="component-editor">
  <!-- Header -->
  <div class="editor-header">
    <button class="back-btn" (click)="onClose()">
      <svg viewBox="0 0 24 24" class="icon">
        <path d="M20,11V13H8L13.5,18.5L12.08,19.92L4.16,12L12.08,4.08L13.5,5.5L8,11H20Z" />
      </svg>
      Back
    </button>

    <div class="header-content">
      <!-- Component Name (Inline Editable) -->
      <div class="component-name-container">
        <h1 *ngIf="!isEditingName" class="component-name editable-field" (click)="startEditingName()" [title]="'Click to edit name'">
          {{ componentForm.get('name')?.value || 'Untitled Component' }}
          <svg class="edit-icon" viewBox="0 0 24 24">
            <path d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z" />
          </svg>
        </h1>
        <input
          *ngIf="isEditingName"
          type="text"
          class="name-input"
          [(ngModel)]="tempName"
          (blur)="finishEditingName()"
          (keydown)="onKeyDown($event, 'name')"
          [placeholder]="'Enter component name'"
          #nameInput
        />
      </div>

      <!-- Component Description (Inline Editable) -->
      <div class="component-description-container">
        <p *ngIf="!isEditingDescription" class="component-description editable-field" (click)="startEditingDescription()" [title]="'Click to edit description'">
          {{ componentForm.get('description')?.value || 'Click to add description' }}
          <svg class="edit-icon" viewBox="0 0 24 24">
            <path d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z" />
          </svg>
        </p>
        <input
          *ngIf="isEditingDescription"
          type="text"
          class="description-input"
          [(ngModel)]="tempDescription"
          (blur)="finishEditingDescription()"
          (keydown)="onKeyDown($event, 'description')"
          [placeholder]="'Enter component description'"
          #descriptionInput
        />
      </div>

      <!-- Category and Active Status -->
      <div class="header-meta">
        <div class="category-selector">
          <span class="meta-label">Category:</span>
          <div class="category-options">
            <button
              *ngFor="let cat of availableCategories"
              type="button"
              class="category-option"
              [class.active]="componentForm.get('category')?.value === cat"
              (click)="onCategoryClick(cat)"
            >
              {{ cat | titlecase }}
            </button>
          </div>
        </div>

        <div class="active-toggle">
          <span class="meta-label">Status:</span>
          <button type="button" class="status-option" [class.active]="componentForm.get('is_active')?.value" (click)="toggleActive()">
            {{ componentForm.get('is_active')?.value ? 'Active' : 'Inactive' }}
          </button>
        </div>
      </div>
    </div>

    <div class="header-actions">
      @if(showSavedMessage) {
      <div class="saved-message">
        <svg viewBox="0 0 24 24" class="saved-icon">
          <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z" />
        </svg>
        Saved
      </div>
      }
      <button type="button" class="btn-primary" (click)="onSave()" [disabled]="saving || componentForm.invalid">
        <span>{{ headerActionsText() }}</span>
      </button>
    </div>
  </div>

  <!-- Main Content -->
  <div class="editor-content" *ngIf="!loading">
    <form [formGroup]="componentForm" (ngSubmit)="onSave()">
      <!-- MJML Template Section -->
      <div class="mjml-field">
        <ngx-mjml-editor
          formControlName="mjml_template"
          [theme]="selectedTheme"
          [availableThemes]="availableThemes"
          [variables]="componentVariables"
          [placeholder]="mjmlPlaceholder"
          [disabled]="saving"
          (themeChange)="onThemeChange($event)"
        >
        </ngx-mjml-editor>
        <div class="field-error" *ngIf="componentForm.get('mjml_template')?.invalid && componentForm.get('mjml_template')?.touched">MJML template is required</div>
      </div>

      <!-- Variables Section -->
      <div class="section">
        <div class="section-header">
          <h2>Variables</h2>
          <button type="button" class="btn-add" (click)="addVariable()">
            <svg viewBox="0 0 24 24" class="icon">
              <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
            </svg>
            Add Variable
          </button>
        </div>

        <div class="variables-list" *ngIf="variablesArray.length > 0">
          <div class="variable-card" *ngFor="let variable of variablesArray.controls; let i = index" [formGroup]="variable">
            <!-- Variable Header: Label and Name -->
            <div class="variable-header">
              <div class="variable-label-container">
                <input type="text" formControlName="label" placeholder="Variable Label" class="variable-label-input" (input)="updateVariableSlugFromLabel(variable)" />
              </div>
              <div class="variable-slug">
                <span class="slug-prefix">&#123;&#123;component.</span>
                <input
                  type="text"
                  formControlName="name"
                  placeholder="variable_name"
                  class="variable-name-input"
                  (input)="sanitizeVariableName(variable)"
                  (blur)="sanitizeVariableName(variable)"
                />
                <span class="slug-suffix">&#125;&#125;</span>
              </div>
            </div>

            <!-- Variable Meta: Type and Required -->
            <div class="variable-meta">
              <div class="variable-type-selector">
                <span class="meta-label">Type:</span>
                <div class="type-options">
                  <button
                    *ngFor="let type of availableTypes"
                    type="button"
                    class="type-option"
                    [class.active]="variable.get('type')?.value === type"
                    (click)="onVariableTypeClick(variable, type)"
                  >
                    {{ type | titlecase }}
                  </button>
                </div>
              </div>

              <div class="variable-required-toggle">
                <span class="meta-label">Required:</span>
                <button type="button" class="required-option" [class.active]="variable.get('is_required')?.value" (click)="toggleVariableRequired(variable)">
                  {{ variable.get('is_required')?.value ? 'Yes' : 'No' }}
                </button>
              </div>
            </div>

            <!-- Default Value -->
            <div class="variable-default" *ngIf="variable.get('type')?.value !== 'boolean'">
              <label class="default-label">Default Value:</label>
              <input type="text" formControlName="default_value" placeholder="Enter default value..." class="default-input" />
            </div>

            <!-- Boolean Default Value -->
            <div class="variable-default-boolean" *ngIf="variable.get('type')?.value === 'boolean'">
              <span class="default-label">Default Value:</span>
              <div class="boolean-options">
                <button
                  type="button"
                  class="boolean-option"
                  [class.active]="variable.get('default_value')?.value === 'true'"
                  (click)="variable.get('default_value')?.setValue('true')"
                >
                  True
                </button>
                <button
                  type="button"
                  class="boolean-option"
                  [class.active]="variable.get('default_value')?.value === 'false'"
                  (click)="variable.get('default_value')?.setValue('false')"
                >
                  False
                </button>
              </div>
            </div>

            <!-- Variable Actions -->
            <div class="variable-actions">
              <button type="button" class="btn-icon" (click)="moveVariableUp(i)" [disabled]="i === 0" title="Move Up">
                <svg viewBox="0 0 24 24">
                  <path d="M7.41,15.41L12,10.83L16.59,15.41L18,14L12,8L6,14L7.41,15.41Z" />
                </svg>
              </button>
              <button type="button" class="btn-icon" (click)="moveVariableDown(i)" [disabled]="i === variablesArray.length - 1" title="Move Down">
                <svg viewBox="0 0 24 24">
                  <path d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z" />
                </svg>
              </button>
              <button type="button" class="btn-icon delete" (click)="removeVariable(i)" title="Delete Variable">
                <svg viewBox="0 0 24 24">
                  <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div class="empty-state" *ngIf="variablesArray.length === 0">
          <p>No variables defined. Add variables to make your component customizable.</p>
          <button type="button" class="btn-add" (click)="addVariable()">Add First Variable</button>
        </div>
      </div>

      <!-- Styling & Preview Section -->
      <div class="section">
        <div class="section-header">
          <h2>Styling & Preview</h2>
          <button type="button" class="btn-secondary" (click)="generatePreviewData()">Generate Sample Data</button>
        </div>
        <div class="code-grid">
          <div class="code-field">
            <label>CSS Styles (JSON)</label>
            <textarea formControlName="css_styles" rows="6" placeholder='{"padding": "20px", "background": "#f8f9fa"}'></textarea>
          </div>
          <div class="code-field">
            <label>Preview Data (JSON)</label>
            <textarea formControlName="preview_data" rows="6" placeholder='{"title": "Sample Title", "description": "Sample text"}'></textarea>
          </div>
        </div>
      </div>
    </form>
  </div>

  <!-- Loading State -->
  <div class="loading-state" *ngIf="loading">
    <div class="spinner"></div>
    <p>Loading component...</p>
  </div>
</div>
